import { useState, useEffect } from 'react';
import Modal from '../shared/Modal';
// API calls are now handled by the parent (AdminSchedule) for optimistic updates
import { TEAMS } from '../../config';

const DAYS = [
  'Thursday', 'Friday', 'Saturday', 'Sunday',
  'Monday', 'Tuesday', 'Wednesday',
];

export default function EditScheduleItemModal({ open, onClose, item, onSave, onDelete, showToast }) {
  const [day, setDay] = useState('');
  const [date, setDate] = useState('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [activity, setActivity] = useState('');
  const [location, setLocation] = useState('');
  const [appliesToAll, setAppliesToAll] = useState(true);
  const [specificTeams, setSpecificTeams] = useState([]);
  const [isMeal, setIsMeal] = useState('No');
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    setConfirmDelete(false);
    if (item) {
      setDay(item.day || 'Thursday');
      setDate(item.date || '');
      setStart(item.start || '');
      setEnd(item.end || '');
      setActivity(item.activity || '');
      setLocation(item.location || '');
      const applies = (item.team || item.appliesToTeams || '').toString().toUpperCase();
      setAppliesToAll(applies.includes('ALL'));
      if (!applies.includes('ALL')) {
        setSpecificTeams((item.team || item.appliesToTeams || '').split(',').map(t => t.trim()).filter(Boolean));
      } else {
        // Clear leftovers from the previously-edited item so switching an ALL
        // item to "Specific Teams…" starts from an empty selection.
        setSpecificTeams([]);
      }
      const mealVal = item.isMeal === true || item.isMeal === 'true' || item.isMeal === 'TRUE';
      setIsMeal(mealVal ? 'Yes' : 'No');
    }
  }, [item]);

  function toggleTeam(team) {
    setSpecificTeams(prev =>
      prev.includes(team) ? prev.filter(t => t !== team) : [...prev, team]
    );
  }

  function handleSave() {
    if (!activity.trim()) { showToast('Item name is required.'); return; }
    if (!appliesToAll && specificTeams.length === 0) {
      showToast('Select at least one team, or choose All Teams.'); return;
    }
    // Don't fall back to ALL — that would broadcast the item to every SL.
    const teamsValue = appliesToAll ? 'ALL' : specificTeams.join(', ');
    const updated = {
      ...item,
      day, date, start, end,
      activity: activity.trim(),
      location: location.trim(),
      team: teamsValue,
      appliesToTeams: teamsValue,
      isMeal: isMeal !== 'No',
    };
    // Pass updated item to parent — parent updates state + calls API (optimistic)
    if (onSave) onSave(updated);
    onClose();
  }

  function handleDelete() {
    if (onDelete) onDelete(item.id);
    onClose();
  }

  if (!item) return null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Edit Schedule Item"
      footer={
        <>
          {confirmDelete ? (
            <>
              <span style={{ fontFamily: 'var(--font-b)', fontSize: 13, fontWeight: 500, color: 'var(--black)' }}>Delete this item?</span>
              <button className="btn btn-sm" onClick={() => setConfirmDelete(false)}>Cancel</button>
              <button className="btn btn-sm btn-danger" onClick={handleDelete}>Yes, Delete</button>
            </>
          ) : (
            <button className="btn btn-danger btn-sm" onClick={() => setConfirmDelete(true)}>Delete</button>
          )}
          {!confirmDelete && <button className="btn" onClick={onClose}>Cancel</button>}
          {!confirmDelete && <button className="btn btn-dark" onClick={handleSave}>Save Changes</button>}
        </>
      }
    >
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }} className="form-group">
        <div>
          <label className="form-label">Day</label>
          <select className="form-input" value={day} onChange={e => setDay(e.target.value)}>
            {DAYS.map(d => <option key={d}>{d}</option>)}
          </select>
        </div>
        <div>
          <label className="form-label">Date</label>
          <input className="form-input" type="date" value={date} onChange={e => setDate(e.target.value)} />
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }} className="form-group">
        <div><label className="form-label">Start Time</label><input className="form-input" type="time" value={start} onChange={e => setStart(e.target.value)} /></div>
        <div><label className="form-label">End Time</label><input className="form-input" type="time" value={end} onChange={e => setEnd(e.target.value)} /></div>
      </div>
      <div className="form-group">
        <label className="form-label">Session / Item Name</label>
        <input className="form-input" value={activity} onChange={e => setActivity(e.target.value)} />
      </div>
      <div className="form-group">
        <label className="form-label">Location</label>
        <input className="form-input" value={location} onChange={e => setLocation(e.target.value)} />
      </div>
      <div className="form-group">
        <label className="form-label">Applies To</label>
        <select className="form-input" value={appliesToAll ? 'ALL' : 'specific'} onChange={e => setAppliesToAll(e.target.value === 'ALL')}>
          <option value="ALL">All Teams</option>
          <option value="specific">Specific Teams…</option>
        </select>
      </div>
      {!appliesToAll && (
        <div className="form-group">
          <label className="form-label">Select Teams</label>
          <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '8px 12px', maxHeight: 180, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 5 }}>
            {TEAMS.map(t => (
              <label key={t} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontFamily: 'var(--font-h)', fontSize: 13, fontWeight: 700, color: 'var(--black)' }}>
                <input type="checkbox" checked={specificTeams.includes(t)} onChange={() => toggleTeam(t)} style={{ accentColor: 'var(--black)' }} />
                {t}
              </label>
            ))}
          </div>
        </div>
      )}
      <div className="form-group">
        <label className="form-label">Meal?</label>
        <select className="form-input" value={isMeal} onChange={e => setIsMeal(e.target.value)}>
          <option>No</option><option>Yes</option>
        </select>
      </div>
    </Modal>
  );
}
